import React, { useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { X, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { MENU_DEFS, ACTION_DEFS, roleAllows } from "../lib/menu";

export default function MenuPermissionsModal({ utente, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);

  // Voci di menu rilevanti per il ruolo dell'utente target
  const defs = useMemo(() => MENU_DEFS.filter((d) => d.key !== "mia_agenda" && roleAllows(utente, d)), [utente]);
  const allKeys = defs.map((d) => d.key);
  const actionDefs = useMemo(() => ACTION_DEFS.filter((d) => !d.adminOnly || utente.role === "admin"), [utente]);
  const allActionKeys = actionDefs.map((d) => d.key);

  const [checked, setChecked] = useState(() => {
    const p = utente.menu_permissions;
    return new Set(p == null ? allKeys : allKeys.filter((k) => p.includes(k)));
  });
  const [checkedActions, setCheckedActions] = useState(() => {
    const p = utente.action_permissions;
    return new Set(p == null ? allActionKeys : allActionKeys.filter((k) => p.includes(k)));
  });

  const toggle = (key) => {
    if (utente.is_super && key === "utenti") return; // anti-blocco
    const next = new Set(checked);
    if (next.has(key)) next.delete(key); else next.add(key);
    setChecked(next);
  };

  const toggleAction = (key) => {
    const next = new Set(checkedActions);
    if (next.has(key)) next.delete(key); else next.add(key);
    setCheckedActions(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const allChecked = allKeys.every((k) => checked.has(k));
      const allActionsChecked = allActionKeys.every((k) => checkedActions.has(k));
      const payload = {
        menu_permissions: allChecked ? null : allKeys.filter((k) => checked.has(k)),
        action_permissions: allActionsChecked ? null : allActionKeys.filter((k) => checkedActions.has(k)),
      };
      await api.put(`/users/${utente.id}/menu-permissions`, payload);
      toast.success(`Permessi menu aggiornati per ${utente.name}`);
      onSaved?.();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="card-shell w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="menu-permissions-modal">
        <div className="p-5 border-b flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <ListChecks className="w-5 h-5" style={{ color: "var(--primary)" }} /> Permessi menu
            </h2>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
              {utente.name} · <span className="capitalize">{utente.role}</span>{utente.is_super ? " · Super admin" : ""}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5" data-testid="close-permissions-modal"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          <div className="flex justify-end gap-2 mb-3 text-xs">
            <button onClick={() => setChecked(new Set(allKeys))} className="font-semibold px-2 py-1 rounded hover:bg-black/5" style={{ color: "var(--primary)" }} data-testid="perm-select-all">Seleziona tutto</button>
            <button onClick={() => setChecked(new Set(utente.is_super ? ["utenti"] : []))} className="font-semibold px-2 py-1 rounded hover:bg-black/5" style={{ color: "#B30000" }} data-testid="perm-deselect-all">Deseleziona tutto</button>
          </div>
          <div className="space-y-1.5">
            {defs.map((d) => {
              const locked = utente.is_super && d.key === "utenti";
              return (
                <label key={d.key} className={`flex items-center gap-2.5 text-sm p-2 rounded-md ${locked ? "opacity-70" : "cursor-pointer hover:bg-black/5"}`}
                  style={{ background: checked.has(d.key) ? "var(--surface-muted)" : "transparent" }}>
                  <input type="checkbox" checked={checked.has(d.key)} disabled={locked} onChange={() => toggle(d.key)}
                    className="w-4 h-4 accent-[var(--primary)]" data-testid={`perm-check-${d.key}`} />
                  <span className="flex-1">{d.label}</span>
                  {locked && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#E6F4EA", color: "#1B5E20" }}>obbligatoria</span>}
                </label>
              );
            })}
          </div>

          <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
              Permessi azioni
            </div>
            <div className="space-y-1.5">
              {actionDefs.map((d) => (
                <label key={d.key} className="flex items-center gap-2.5 text-sm p-2 rounded-md cursor-pointer hover:bg-black/5"
                  style={{ background: checkedActions.has(d.key) ? "var(--surface-muted)" : "transparent" }}>
                  <input type="checkbox" checked={checkedActions.has(d.key)} onChange={() => toggleAction(d.key)}
                    className="w-4 h-4 accent-[var(--primary)]" data-testid={`action-check-${d.key}`} />
                  <span className="flex-1">{d.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-permissions">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salva permessi
          </button>
        </div>
      </div>
    </div>
  );
}
